import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { api } from "../lib/api"
import { Avatar } from "../components/ui"
import { RelativeTime } from "../components/ui"
import type { TaskLevel } from "../lib/api"
import { LEVEL_LABELS } from "../lib/levels"

type Mention = {
  id: string
  commentId: string
  readAt: string | null
  createdAt: string
  comment: {
    body: string
    author: {
      id: string
      name: string
    }
  }
  context: {
    /** The level of the item the comment is on. */
    type: TaskLevel
    /** The item itself: every level has its own page. */
    taskId: string
    key?: string
    title: string
    project: {
      id: string
      key: string
    }
  }
}

export function InboxPage() {
  const navigate = useNavigate()
  const [mentions, setMentions] = useState<Mention[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadMentions()
  }, [])

  async function loadMentions() {
    try {
      const res = await api.get<{ mentions: Mention[] }>("/mentions")
      setMentions(res.mentions)
    } catch (err) {
      console.error("Failed to load mentions:", err)
    } finally {
      setLoading(false)
    }
  }

  async function markAsRead(ids: string[]) {
    try {
      await api.post("/mentions/read", { ids })
      // Update local state
      setMentions((prev) =>
        prev.map((m) => (ids.includes(m.id) ? { ...m, readAt: new Date().toISOString() } : m)),
      )
    } catch (err) {
      console.error("Failed to mark as read:", err)
    }
  }

  async function markAllAsRead() {
    const unreadIds = mentions.filter((m) => !m.readAt).map((m) => m.id)
    if (unreadIds.length > 0) {
      await markAsRead(unreadIds)
    }
  }

  function handleClick(mention: Mention) {
    // Mark as read when clicked
    if (!mention.readAt) {
      markAsRead([mention.id])
    }
    navigate(`/tasks/${mention.context.taskId}`)
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-sm text-muted">Loading...</div>
      </div>
    )
  }

  const unreadCount = mentions.filter((m) => !m.readAt).length

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Inbox</h1>
          {unreadCount > 0 && (
            <p className="mt-1 text-sm text-muted">
              {unreadCount} unread mention{unreadCount !== 1 ? "s" : ""}
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent/90"
          >
            Mark all as read
          </button>
        )}
      </div>

      {mentions.length === 0 ? (
        <div className="rounded-lg border border-border bg-surface-muted p-12 text-center">
          <div className="text-sm text-muted">No mentions yet</div>
          <p className="mt-2 text-xs text-faint">
            When someone mentions you in a comment, it will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {mentions.map((mention) => (
            <div
              key={mention.id}
              onClick={() => handleClick(mention)}
              className={`cursor-pointer rounded-lg border border-border p-4 transition-colors hover:bg-surface-muted ${
                !mention.readAt ? "bg-accent/5" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                <Avatar name={mention.comment.author.name} size="md" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-foreground">
                      {mention.comment.author.name}
                    </span>
                    <span className="text-muted">mentioned you in</span>
                    <span className="font-medium text-foreground">
                      {mention.context.project.key}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-foreground">
                    {LEVEL_LABELS[mention.context.type] ?? "Task"}
                    {mention.context.key ? <span className="font-mono text-xs text-faint"> {mention.context.key}</span> : null}:{" "}
                    <span className="font-medium">{mention.context.title}</span>
                  </div>
                  <div className="mt-2 line-clamp-2 text-sm text-muted">
                    {mention.comment.body}
                  </div>
                  <div className="mt-2 text-xs text-faint">
                    <RelativeTime value={mention.createdAt} />
                  </div>
                </div>
                {!mention.readAt && (
                  <div className="h-2 w-2 shrink-0 rounded-full bg-accent" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
